/**
 * The Test-UI harness `HostAdapter` — `localStorage` persistence + JSON round-trip.
 *
 * Built on the shared `createStorageAdapter`: the harness adds only the default
 * storage (the browser's `localStorage`, or an injected in-memory `StorageLike`
 * in tests) and the honest isolation tier. Persists ONLY the event-log snapshot;
 * verdicts are always re-derived from the replayed log (INV-NOEQUIV).
 */

import type { HostAdapter } from '@progression/contracts';
import {
  createStorageAdapter,
  MemoryStorage,
  exportSnapshotJson,
  importSnapshotJson,
  type StorageLike,
} from '@progression/host-bundle';

export { exportSnapshotJson, importSnapshotJson, MemoryStorage };
export type { StorageLike };

/** localStorage key under which the harness stores its event-log snapshot. */
export const STORAGE_KEY = 'progression.testui.snapshot.v1';

/**
 * A persistence adapter for the harness. In a browser it uses `localStorage`
 * (BEST-EFFORT — shared realm); in a non-DOM environment (Node tests, a Worker)
 * it falls back to an in-memory `MemoryStorage`. The Cloudflare deployment passes
 * `tier: 'STRONG'` to report the hardened-isolate tier honestly.
 */
export function createHarnessAdapter(
  options: { storage?: StorageLike; tier?: 'STRONG' | 'BEST-EFFORT' } = {},
): HostAdapter {
  const storage =
    options.storage ??
    (typeof globalThis.localStorage !== 'undefined' ? globalThis.localStorage : new MemoryStorage());
  return createStorageAdapter(storage, { key: STORAGE_KEY, tier: options.tier ?? 'BEST-EFFORT' });
}
